import type { HeadLine, HeadLineModify, LineCheck } from '../../interfaces'
import type { RootState } from '../../store'

import { Table, Tag } from 'antd'
import DatePicker from 'antd/lib/date-picker'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Payments from '../../API/payments'
import { label as getLabel, CRReportType } from '../../domain/cr-report-type.enum'
import usePagination from '../../hook/use-pagination'
import { clearCheckData, fetchCheckRegister } from '../../store/check-register/check-slice'
import { selectCheckGrid, selectStatus, selectTotal } from '../../store/check-register/selector'
import { addExtraProps, getComponentForCellProps } from '../CellsGrid'
import DebounceSelect from '../filterComponent/Filter'
import TableMenu from '../TableMenu/TableMenu'

import '../../css/CheckRegister.scss'

import Pagination from './../Pagination/Pagination'
import Preloader from './../Preloader/Preloader'
import { headerCheck } from './header'

interface TagRenderProps {
  label: React.ReactNode
  value: string
  closable?: boolean
  onClose?: () => void
}

export function tagRender(props: TagRenderProps): React.ReactElement {
  const { label, value, closable, onClose } = props

  const onPreventMouseDown = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }
  return (
    <Tag
      className='tag-custom-checkregister'
      color={value}
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      style={{ marginRight: 3 }}
    >
      {label}
    </Tag>
  )
}

const setCell = (header: HeadLine[]) => {
  const newHeader: HeadLineModify[] = []
  header.map(item => {
    const line: HeadLineModify = {
      render: (title: string, record: LineCheck): React.ReactNode => {
        const dataIndex = Array.isArray(item.dataIndex) ? item.dataIndex[0] : item.dataIndex
        const Component = getComponentForCellProps(dataIndex || '')
        return addExtraProps(
          <Component key={record.groupName || record.entId || 'default'} title={title} />,
          record
        )
      },
      title: item.title,
      dataIndex: item.dataIndex,
      key: item.key,
      width: item.width
    }
    newHeader.push(line)
  })
  return newHeader
}

export function CheckRegisterComponent(): JSX.Element {
  const dispatch = useDispatch()
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [filter, setFilter] = useState('')
  const data = useSelector<RootState, LineCheck[]>(selectCheckGrid)
  const [openDropdown, setOpenDropdown] = useState(false)
  const totalElem = useSelector<RootState, string>(selectTotal)
  const status = useSelector<RootState, boolean>(selectStatus)
  const [currentPage, pageSize, total, setPageSize, setCurrentPage] = usePagination(
    Number(totalElem),
    10
  )
  useEffect(() => {
    clearCheckData()
    dispatch(
      fetchCheckRegister({
        countRows: pageSize,
        currentPage: currentPage - 1,
        fromDate: dateFrom === '' ? undefined : dateFrom,
        toDate: dateTo === '' ? undefined : dateTo,
        groupName: filter === '' ? undefined : filter
      })
    )
  }, [pageSize, currentPage, dateFrom, dateTo, filter, dispatch])
  useEffect(() => {
    clearCheckData()
    dispatch(
      fetchCheckRegister({
        countRows: pageSize,
        currentPage: 0,
        fromDate: dateFrom === '' ? undefined : dateFrom,
        toDate: dateTo === '' ? undefined : dateTo,
        groupName: filter === '' ? undefined : filter
      })
    )
  }, [dateFrom, dateTo, filter, dispatch, pageSize])
  return (
    <>
      <TableMenu>
        <>
          <div className='filter-group'>
            <DebounceSelect
              allowClear
              showArrow={false}
              value={filter}
              placeholder='Group#|Group Name'
              optionFilterProp='children'
              tagRender={tagRender}
              onSelect={useCallback((value: { value: string; label: string } | string) => {
                const val = typeof value === 'string' ? value : value.value
                setFilter(val)
              }, [])}
              fetchOptions={async username => Payments.getFilter({ filterLine: username })}
              onChange={newValue => {
                if (newValue === undefined) {
                  setFilter('')
                } else if (typeof newValue === 'string') {
                  setFilter(newValue)
                } else if (Array.isArray(newValue)) {
                  setFilter('')
                } else {
                  setFilter(newValue.value)
                }
              }}
              onOpenChange={open => {
                setOpenDropdown(open)
              }}
              className='select-custom group'
              style={{ width: 310 }}
            />
            {!openDropdown && filter === '' && (
              <span className='placeholder'>Group#|Group Name</span>
            )}
          </div>
          <div className='date-search'>
            <DatePicker
              suffixIcon={<img src='../calendar.png' />}
              allowClear={false}
              className='picker-custom picker-custom--first'
              placeholder='From'
              onChange={(date, dateString) => {
                const dateValue = Array.isArray(dateString) ? dateString[0] : dateString
                setDateFrom(dateValue || '')
              }}
            />
            <DatePicker
              suffixIcon={<img src='../calendar.png' />}
              allowClear={false}
              className='picker-custom'
              placeholder='To'
              onChange={(date, dateString) => {
                const dateValue = Array.isArray(dateString) ? dateString[0] : dateString
                setDateTo(dateValue || '')
              }}
            />
          </div>
        </>
      </TableMenu>
      <Table
        size='middle'
        className='table-custom'
        columns={setCell(headerCheck)}
        dataSource={data.map(item => ({
          ...item,
          reportType: getLabel(item.type as CRReportType, item.options)
        }))}
        pagination={false}
        loading={{
          indicator: <Preloader />,
          spinning: status
        }}
      />
      {Number(total) > pageSize && (
        <Pagination
          size={pageSize}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          setSizePage={setPageSize}
          total={total}
          pageSizeOptions={[10, 20, 50, 100]}
        />
      )}
    </>
  )
}
